import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, Users, CheckCircle, Calendar, TrendingUp, AlertCircle, Eye, Phone, MessageCircle, Mail, Linkedin as LinkedinIcon, Layers, ArrowDownCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { useCampaignStats } from '../../hooks/useCampaignStats';
import LoadingScreen from '../shared/LoadingScreen';
import StatusDropdown from '../shared/StatusDropdown';

type CampaignType = 'call' | 'whatsapp' | 'email' | 'linkedin' | 'omni' | 'inbound';

const CAMPAIGN_TYPES: CampaignType[] = ['call', 'whatsapp', 'email', 'linkedin', 'omni', 'inbound'];

const CampaignsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { campaignStats, summary, isLoading, error } = useCampaignStats();
  const [campaignStatuses, setCampaignStatuses] = useState<Record<CampaignType, string>>({
    call: 'active',
    whatsapp: 'active',
    email: 'active',
    linkedin: 'active',
    omni: 'active',
    inbound: 'active'
  });
  const initDoneRef = useRef(false);

  useEffect(() => {
    if (!user || initDoneRef.current) return;
    initDoneRef.current = true;

    const initializeCampaigns = async () => {
      try {
        const { data: existingCampaigns, error: fetchError } = await supabase
          .from('campaigns')
          .select('campaign_id, type, status')
          .eq('client_id', user.client_id)
          .in('type', CAMPAIGN_TYPES as unknown as string[]);

        if (fetchError) throw fetchError;

        const existingTypes = new Set(existingCampaigns?.map(c => c.type) || []);
        const newStatuses: Record<CampaignType, string> = { ...campaignStatuses };

        for (const campaign of existingCampaigns || []) {
          newStatuses[campaign.type as CampaignType] = campaign.status || 'active';
        }

        const missingTypes = CAMPAIGN_TYPES.filter(type => !existingTypes.has(type));

        if (missingTypes.length > 0) {
          const newCampaigns = missingTypes.map(type => ({
            client_id: user.client_id,
            user_id: user.email,
            type,
            name: `${type.charAt(0).toUpperCase() + type.slice(1)} Campaign`,
            status: 'active'
          }));

          await supabase.from('campaigns').insert(newCampaigns);
        }

        setCampaignStatuses(newStatuses);
      } catch (err) {
        console.error('Error initializing campaigns:', err);
      }
    };

    initializeCampaigns();
  }, [user]);

  const handleStatusChange = async (campaignType: CampaignType, newStatus: string) => {
    if (!user) return;

    try {
      const { error: updateError } = await supabase
        .from('campaigns')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('client_id', user.client_id)
        .eq('type', campaignType);

      if (updateError) throw updateError;

      await cascadeCampaignStatusToLeads(campaignType, newStatus);

      setCampaignStatuses(prev => ({
        ...prev,
        [campaignType]: newStatus
      }));
    } catch (err) {
      console.error('Error updating campaign status:', err);
      throw err;
    }
  };

  const cascadeCampaignStatusToLeads = async (campaignType: CampaignType, newStatus: string) => {
    if (!user) return;
    const now = new Date().toISOString();
    const TERMINAL_STATUSES = ['closed', 'failed', 'completed'];

    if (newStatus === 'paused') {
      const { data: lcRows } = await supabase
        .from('leads_campaigns')
        .select('lead_id, status')
        .eq('client_id', user.client_id)
        .eq('campaign_type', campaignType);

      const leadIds = (lcRows || [])
        .filter(r => r.lead_id && !TERMINAL_STATUSES.includes((r.status || '').toLowerCase()))
        .map(r => r.lead_id);

      if (leadIds.length === 0) return;

      await supabase
        .from('leads_campaigns')
        .update({ status: 'on hold', updated_at: now })
        .in('lead_id', leadIds)
        .eq('campaign_type', campaignType);
      await supabase
        .from('leads_tracking')
        .update({ ovrall_lead_status: 'on hold', updated_at: now })
        .in('lead_id', leadIds);
    } else if (newStatus === 'active') {
      const { data: lcRows } = await supabase
        .from('leads_campaigns')
        .select('lead_id, campaign_step')
        .eq('client_id', user.client_id)
        .eq('campaign_type', campaignType)
        .eq('status', 'on hold');

      const step0Ids = (lcRows || []).filter(r => r.lead_id && (r.campaign_step || 0) === 0).map(r => r.lead_id);
      const stepGte1Ids = (lcRows || []).filter(r => r.lead_id && (r.campaign_step || 0) >= 1).map(r => r.lead_id);

      if (step0Ids.length > 0) {
        await supabase
          .from('leads_campaigns')
          .update({ status: 'assigned', updated_at: now })
          .in('lead_id', step0Ids)
          .eq('campaign_type', campaignType);
        await supabase
          .from('leads_tracking')
          .update({ ovrall_lead_status: 'assigned', updated_at: now })
          .in('lead_id', step0Ids);
      }
      if (stepGte1Ids.length > 0) {
        await supabase
          .from('leads_campaigns')
          .update({ status: 'follow up', updated_at: now })
          .in('lead_id', stepGte1Ids)
          .eq('campaign_type', campaignType);
        await supabase
          .from('leads_tracking')
          .update({ ovrall_lead_status: 'follow up', updated_at: now })
          .in('lead_id', stepGte1Ids);
      }
    }
  };

  const callData = campaignStats.call;
  const whatsappData = campaignStats.whatsapp;
  const emailData = campaignStats.email;
  const linkedinData = campaignStats.linkedin;
  const omniData = campaignStats.omni;
  const inboundData = campaignStats.inbound;

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-lg flex items-center space-x-2 mb-6">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-xl border border-[#1875FF]/20 p-5 flex items-center space-x-4">
          <div className="w-11 h-11 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center flex-shrink-0">
            <Target className="text-white" size={22} />
          </div>
          <div>
            <div className="text-3xl font-bold text-gray-900 font-metric">{CAMPAIGN_TYPES.length}</div>
            <div className="text-sm text-gray-600 font-medium">Total Campaigns</div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-xl border border-[#1875FF]/20 p-5 flex items-center space-x-4">
          <div className="w-11 h-11 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center flex-shrink-0">
            <Users className="text-white" size={22} />
          </div>
          <div>
            <div className="text-3xl font-bold text-gray-900 font-metric">{summary.totalLeads}</div>
            <div className="text-sm text-gray-600 font-medium">Leads in Campaigns</div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-xl border border-[#1875FF]/20 p-5 flex items-center space-x-4">
          <div className="w-11 h-11 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center flex-shrink-0">
            <CheckCircle className="text-white" size={22} />
          </div>
          <div>
            <div className="text-3xl font-bold text-gray-900 font-metric">{summary.activeLeads}</div>
            <div className="text-sm text-gray-600 font-medium">Active Leads</div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-xl border border-[#1875FF]/20 p-5 flex items-center space-x-4">
          <div className="w-11 h-11 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center flex-shrink-0">
            <Calendar className="text-white" size={22} />
          </div>
          <div>
            <div className="text-3xl font-bold text-gray-900 font-metric">{summary.meetingsBooked}</div>
            <div className="text-sm text-gray-600 font-medium">Meetings Booked</div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-xl border border-[#1875FF]/20 p-5 flex items-center space-x-4">
          <div className="w-11 h-11 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center flex-shrink-0">
            <TrendingUp className="text-white" size={22} />
          </div>
          <div>
            <div className="text-3xl font-bold text-gray-900 font-metric">{summary.replyRate}%</div>
            <div className="text-sm text-gray-600 font-medium">Overall Reply Rate</div>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 shadow-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-[#05E0D1]/10 to-[#1875FF]/10 border-b-2 border-[#1875FF]/10">
              <tr>
                <th className="text-left px-6 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans">
                  Campaign Name
                </th>
                <th className="text-center px-6 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans">
                  Total Leads
                </th>
                <th className="text-center px-6 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans">
                  Active Leads
                </th>
                <th className="text-center px-6 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans">
                  Completed Leads
                </th>
                <th className="text-center px-6 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans">
                  Engaged Leads
                </th>
                <th className="text-center px-6 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans">
                  Meetings Booked
                </th>
                <th className="text-center px-6 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans">
                  Reply Rate
                </th>
                <th className="text-center px-6 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans">
                  Conversion Rate
                </th>
                <th className="text-center px-6 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans">
                  Status
                </th>
                <th className="text-center px-6 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1875FF]/10">
              <tr className="hover:bg-[#1875FF]/5 transition-colors cursor-pointer" onClick={() => navigate('/campaigns/call')}>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                      <Phone size={19} className="text-white" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900 whitespace-nowrap">Call Campaign</div>
                      <div className="text-xs text-gray-500 whitespace-nowrap">Phone outreach</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{callData.totalLeads}</td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{callData.activeLeads}</td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{callData.completedLeads}</td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{callData.engagedLeads}</td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{callData.meetingsBooked}</td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{callData.replyRate}%</td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{callData.conversionRate}%</td>
                <td className="px-6 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                  <StatusDropdown
                    currentStatus={campaignStatuses.call}
                    onStatusChange={(newStatus) => handleStatusChange('call', newStatus)}
                    campaignType="call"
                  />
                </td>
                <td className="px-6 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => navigate('/campaigns/call')}
                    className="inline-flex items-center px-3 py-1.5 text-sm bg-gradient-to-br from-[#1875FF] to-[#05E0D1] text-white rounded-xl shadow-sm hover:shadow-md transition-all"
                  >
                    <Eye size={14} className="mr-1" />
                    Overview
                  </button>
                </td>
              </tr>
              <tr className="hover:bg-[#1875FF]/5 transition-colors cursor-pointer" onClick={() => navigate('/campaigns/whatsapp')}>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                      <MessageCircle size={19} className="text-white" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900 whitespace-nowrap">WhatsApp Campaign</div>
                      <div className="text-xs text-gray-500 whitespace-nowrap">WhatsApp messaging</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{whatsappData.totalLeads}</td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{whatsappData.activeLeads}</td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{whatsappData.completedLeads}</td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{whatsappData.engagedLeads}</td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{whatsappData.meetingsBooked}</td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{whatsappData.replyRate}%</td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{whatsappData.conversionRate}%</td>
                <td className="px-6 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                  <StatusDropdown
                    currentStatus={campaignStatuses.whatsapp}
                    onStatusChange={(newStatus) => handleStatusChange('whatsapp', newStatus)}
                    campaignType="whatsapp"
                  />
                </td>
                <td className="px-6 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => navigate('/campaigns/whatsapp')}
                    className="inline-flex items-center px-3 py-1.5 text-sm bg-gradient-to-br from-[#1875FF] to-[#05E0D1] text-white rounded-xl shadow-sm hover:shadow-md transition-all"
                  >
                    <Eye size={14} className="mr-1" />
                    Overview
                  </button>
                </td>
              </tr>
              <tr className="hover:bg-[#1875FF]/5 transition-colors cursor-pointer" onClick={() => navigate('/campaigns/email')}>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                      <Mail size={19} className="text-white" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900 whitespace-nowrap">Email Campaign</div>
                      <div className="text-xs text-gray-500 whitespace-nowrap">Email outreach</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{emailData.totalLeads}</td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{emailData.activeLeads}</td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{emailData.completedLeads}</td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{emailData.engagedLeads}</td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{emailData.meetingsBooked}</td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{emailData.replyRate}%</td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{emailData.conversionRate}%</td>
                <td className="px-6 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                  <StatusDropdown
                    currentStatus={campaignStatuses.email}
                    onStatusChange={(newStatus) => handleStatusChange('email', newStatus)}
                    campaignType="email"
                  />
                </td>
                <td className="px-6 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => navigate('/campaigns/email')}
                    className="inline-flex items-center px-3 py-1.5 text-sm bg-gradient-to-br from-[#1875FF] to-[#05E0D1] text-white rounded-xl shadow-sm hover:shadow-md transition-all"
                  >
                    <Eye size={14} className="mr-1" />
                    Overview
                  </button>
                </td>
              </tr>
              <tr className="hover:bg-[#1875FF]/5 transition-colors cursor-pointer" onClick={() => navigate('/campaigns/linkedin')}>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                      <LinkedinIcon size={19} className="text-white" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900 whitespace-nowrap">LinkedIn Campaign</div>
                      <div className="text-xs text-gray-500 whitespace-nowrap">LinkedIn outreach</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{linkedinData.totalLeads}</td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{linkedinData.activeLeads}</td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{linkedinData.completedLeads}</td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{linkedinData.engagedLeads}</td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{linkedinData.meetingsBooked}</td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{linkedinData.replyRate}%</td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{linkedinData.conversionRate}%</td>
                <td className="px-6 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                  <StatusDropdown
                    currentStatus={campaignStatuses.linkedin}
                    onStatusChange={(newStatus) => handleStatusChange('linkedin', newStatus)}
                    campaignType="linkedin"
                  />
                </td>
                <td className="px-6 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => navigate('/campaigns/linkedin')}
                    className="inline-flex items-center px-3 py-1.5 text-sm bg-gradient-to-br from-[#1875FF] to-[#05E0D1] text-white rounded-xl shadow-sm hover:shadow-md transition-all"
                  >
                    <Eye size={14} className="mr-1" />
                    Overview
                  </button>
                </td>
              </tr>
              <tr className="hover:bg-[#1875FF]/5 transition-colors cursor-pointer" onClick={() => navigate('/campaigns/omni-channel')}>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                      <Layers size={19} className="text-white" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900 whitespace-nowrap">Omni-Channel Campaign</div>
                      <div className="text-xs text-gray-500 whitespace-nowrap">Multi-channel approach</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{omniData.totalLeads}</td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{omniData.activeLeads}</td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{omniData.completedLeads}</td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{omniData.engagedLeads}</td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{omniData.meetingsBooked}</td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{omniData.replyRate}%</td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{omniData.conversionRate}%</td>
                <td className="px-6 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                  <StatusDropdown
                    currentStatus={campaignStatuses.omni}
                    onStatusChange={(newStatus) => handleStatusChange('omni', newStatus)}
                    campaignType="omni"
                  />
                </td>
                <td className="px-6 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => navigate('/campaigns/omni-channel')}
                    className="inline-flex items-center px-3 py-1.5 text-sm bg-gradient-to-br from-[#1875FF] to-[#05E0D1] text-white rounded-xl shadow-sm hover:shadow-md transition-all"
                  >
                    <Eye size={14} className="mr-1" />
                    Overview
                  </button>
                </td>
              </tr>
              <tr className="hover:bg-[#1875FF]/5 transition-colors cursor-pointer" onClick={() => navigate('/campaigns/inbound')}>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                      <ArrowDownCircle size={19} className="text-white" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900 whitespace-nowrap">Inbound Campaign</div>
                      <div className="text-xs text-gray-500 whitespace-nowrap">Inbound leads</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{inboundData.totalLeads}</td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{inboundData.activeLeads}</td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{inboundData.completedLeads}</td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{inboundData.engagedLeads}</td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{inboundData.meetingsBooked}</td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{inboundData.replyRate}%</td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">{inboundData.conversionRate}%</td>
                <td className="px-6 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                  <StatusDropdown
                    currentStatus={campaignStatuses.inbound}
                    onStatusChange={(newStatus) => handleStatusChange('inbound', newStatus)}
                    campaignType="inbound"
                  />
                </td>
                <td className="px-6 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => navigate('/campaigns/inbound')}
                    className="inline-flex items-center px-3 py-1.5 text-sm bg-gradient-to-br from-[#1875FF] to-[#05E0D1] text-white rounded-xl shadow-sm hover:shadow-md transition-all"
                  >
                    <Eye size={14} className="mr-1" />
                    Overview
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CampaignsPage;
